/**
 * Mar 2026 — Fallback / Maintenance page
 *
 * Used in two scenarios:
 *  1. The frontend can't reach the backend (network down, DB unreachable,
 *     server crashed) — `BootHealthGate` shows this with `mode="down"`.
 *  2. Admin has flipped on maintenance mode (`/api/site-state` →
 *     `maintenance_mode: true`) — `BootHealthGate` shows this with
 *     `mode="maintenance"`.
 *
 * Boutique-y, on-brand, NOT a soulless "we are down" page. The copy
 * leans into the boutique narrative — needle, thread, alterations.
 */
import { Scissors, Coffee, Sparkles, Phone, RefreshCw, Heart } from "lucide-react";
import { useSettings } from "../auth/SettingsContext";


export default function ServiceUnavailable({
    mode = "down",            // "down" | "maintenance"
    customMessage = "",       // optional admin-set message for maintenance
    onRetry,                  // callback to re-run the health probe
}) {
    // May 27 2026 — pull WhatsApp + brand email from live settings so
    // this fallback page reflects whatever admin set without a redeploy.
    const _settings = useSettings();
    const _waNumber = (_settings.whatsapp_number || "917359792437").replace(/\D/g, "");
    const _brandEmail = _settings.contact_email || "admin.jns.creation@gmail.com";
    const isMaint = mode === "maintenance";
    const eyebrow = isMaint ? "Atelier closed for alterations" : "We're catching a stitch";
    const title   = isMaint ? "JnS is taking a quick tea break ☕" : "Our needle slipped — back in a moment ✨";
    const blurb   = customMessage || (isMaint
        ? "Our boutique team is making some thoughtful upgrades behind the curtain. We'll be back with a fresh thread shortly."
        : "We can't quite reach our atelier right now. It's almost certainly us, not you — please give it a moment and try again.");

    return (
        <div className="min-h-screen w-full bg-ivory text-ink relative overflow-hidden flex items-center justify-center px-6"
            data-testid="service-unavailable-page" data-mode={mode}>

            {/* Soft brand backdrop — wine ink wash + rosegold flecks */}
            <div className="absolute inset-0 pointer-events-none opacity-50"
                style={{
                    backgroundImage:
                        "radial-gradient(60% 80% at 50% 0%, rgba(201,164,122,0.16) 0%, transparent 60%), " +
                        "radial-gradient(40% 60% at 95% 100%, rgba(107,44,57,0.08) 0%, transparent 60%)",
                }} />

            <div className="relative max-w-2xl w-full text-center cs-modal-bounce">
                {/* Decorative scissor mark */}
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-white border border-rosegold/30 shadow-sm mb-6">
                    {isMaint ? <Coffee size={26} className="text-rosegold-deep" /> : <Scissors size={26} className="text-rosegold-deep" />}
                </div>

                <p className="text-[10px] uppercase tracking-[0.36em] text-rosegold-deep font-bold mb-2">{eyebrow}</p>
                <h1 className="font-display text-4xl sm:text-5xl text-ink leading-[1.1] mb-4">
                    {title}
                </h1>
                <p className="text-[14.5px] text-ink-soft leading-relaxed max-w-md mx-auto whitespace-pre-wrap">
                    {blurb}
                </p>

                {/* Light secondary callouts that keep the page feeling alive,
                     not empty. */}
                <div className="mt-7 flex flex-wrap items-center justify-center gap-2 text-[11px] text-ink-soft">
                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-stone-200">
                        <Sparkles size={11} className="text-rosegold" /> Handcrafted in Anand
                    </span>
                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-stone-200">
                        <Heart size={11} className="text-rosegold" /> Boutique-grade quality
                    </span>
                </div>

                <div className="mt-7 flex flex-wrap items-center justify-center gap-2">
                    {onRetry && (
                        <button onClick={onRetry}
                            data-testid="service-unavailable-retry"
                            className="inline-flex items-center gap-1.5 h-11 px-6 rounded-full bg-ink text-ivory hover:bg-ink/90 text-[13px] font-medium shadow-md hover:shadow-lg transition-all">
                            <RefreshCw size={13} /> Try again
                        </button>
                    )}
                    <a href={`https://wa.me/${_waNumber}`}
                        target="_blank" rel="noopener noreferrer"
                        data-testid="service-unavailable-whatsapp"
                        className="inline-flex items-center gap-1.5 h-11 px-6 rounded-full bg-white border border-rose-200 text-wine hover:bg-rose-50 text-[13px] font-medium transition-all">
                        <Phone size={13} /> WhatsApp the boutique
                    </a>
                </div>

                <div className="mt-10 text-[11px] text-ink-soft">
                    JnS Creation Boutique · <span className="text-rosegold-deep">{_brandEmail}</span>
                </div>
            </div>
        </div>
    );
}
